import { createAsyncThunk } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";

import { type ProductClientNote } from "@fitness-app/data-models/entities/ProductClient";

import { getLoggedUser } from "../../../helpers/getLoggedUser";
import { type ActionParams, type AsyncThunkCreator } from "../../../index";
import { PRODUCT_CLIENT_REDUCER } from "../types";

type Payload = {
  productClientId: string;
  content: string;
};

/**
 * Appends a manual audit note to a product client (TRE-1511). Reads the
 * current list first — notes are edited by at most one admin at a time, so
 * the read-modify-write matches the other trainer-side direct updates.
 */
export const addProductClientNote = createAsyncThunk<
  { notes: ProductClientNote[] },
  ActionParams<Payload>,
  AsyncThunkCreator<string>
>(
  `${PRODUCT_CLIENT_REDUCER}/addProductClientNote`,
  async ({ payload, onSuccess, onFailure }, { rejectWithValue, extra: { db, auth } }) => {
    const { email } = await getLoggedUser(auth);

    const { data, error: fetchError } = await db
      .from("product_client")
      .select("notes")
      .eq("id", payload.productClientId)
      .single();

    if (fetchError) {
      onFailure?.();

      return rejectWithValue(fetchError.message);
    }

    const note: ProductClientNote = {
      id: uuid(),
      createdAt: new Date().toISOString(),
      author: email ?? "unknown",
      kind: null,
      content: payload.content.trim(),
    };

    const notes = [...((data?.notes as ProductClientNote[] | null) ?? []), note];

    // `select()` is what makes an RLS refusal visible: without it PostgREST
    // answers 204 for a row the policy filtered out, so a blocked write looks
    // exactly like a successful one (TRE-2143).
    const { data: updated, error } = await db
      .from("product_client")
      .update({ notes })
      .eq("id", payload.productClientId)
      .select("id");

    if (error) {
      onFailure?.();

      return rejectWithValue(error.message);
    }

    if (!updated.length) {
      onFailure?.();

      return rejectWithValue("No product client row was updated");
    }

    onSuccess?.();

    return { notes };
  },
);
